import React from "react";
import { motion } from "framer-motion";
import { Users, Rocket, ShieldCheck, Star, Globe } from "lucide-react";
import HeroSection from "../components/HeroSection/HeroSection";
import Footer from "../components/Footer/Footer";

const AboutUs = () => {
    const fadeInUp = {
        initial: { opacity: 0, y: 40 },
        animate: { opacity: 1, y: 0 },
        transition: { duration: 0.8, ease: "easeOut" }
    };

    const staggerContainer = {
        animate: {
            transition: { staggerChildren: 0.2 }
        }
    };

    return (
        <>
            <HeroSection></HeroSection>
            <section className="relative w-full min-h-screen flex flex-col items-center py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-white via-orange-50 to-orange-100 overflow-hidden">


                {/* Background Blobs */}
                <div className="absolute inset-0 overflow-hidden">
                    <div className="absolute top-1/4 left-1/4 w-72 h-72 bg-orange-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob"></div>
                    <div className="absolute top-1/3 right-1/4 w-72 h-72 bg-yellow-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-2000"></div>
                    <div className="absolute bottom-1/4 left-1/2 w-72 h-72 bg-orange-300 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-4000"></div>
                </div>

                {/* Page Container */}
                <motion.div
                    variants={staggerContainer}
                    initial="initial"
                    animate="animate"
                    className="max-w-6xl mx-auto text-center relative z-10 space-y-12"
                >
                    {/* Title */}
                    <motion.h1
                        variants={fadeInUp}
                        className="text-4xl sm:text-6xl lg:text-7xl font-bold tracking-tight"
                    >
                        <span className="bg-gradient-to-r from-orange-600 to-orange-500 bg-clip-text text-transparent">
                            About Monitize Media
                        </span>
                    </motion.h1>

                    {/* Subtitle */}
                    <motion.p
                        variants={fadeInUp}
                        className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed"
                    >
                        We empower publishers with high-impact, premium ad solutions that boost revenue while keeping user experience at the core.
                    </motion.p>

                    {/* Mission Section */}
                    <motion.div
                        variants={fadeInUp}
                        className="bg-white/70 backdrop-blur-md border border-orange-200 shadow-xl rounded-3xl p-10 max-w-4xl mx-auto"
                    >
                        <h2 className="text-3xl sm:text-4xl font-bold text-orange-600 mb-4">Our Mission</h2>
                        <p className="text-gray-700 text-lg leading-relaxed">
                            Our mission is simple: to help publishers unlock the *highest earning potential* for their traffic with clean, fast, and engaging ad formats.
                            We partner with top global advertisers to deliver the most premium ads while ensuring a seamless experience for website visitors.
                        </p>
                    </motion.div>

                    {/* Values / Features */}
                    <motion.div
                        variants={fadeInUp}
                        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 pt-10"
                    >
                        {[
                            {
                                icon: <Users className="w-10 h-10 text-orange-600" />,
                                title: "Publisher First",
                                desc: "We build solutions that prioritize your growth and long-term earnings."
                            },
                            {
                                icon: <Rocket className="w-10 h-10 text-orange-600" />,
                                title: "High Performance",
                                desc: "Fast-loading, high-quality ads with superior engagement metrics."
                            },
                            {
                                icon: <ShieldCheck className="w-10 h-10 text-orange-600" />,
                                title: "Brand Safe",
                                desc: "We only collaborate with trusted premium advertisers worldwide."
                            },
                            {
                                icon: <Star className="w-10 h-10 text-orange-600" />,
                                title: "Reliable Earnings",
                                desc: "Consistent, transparent & fast payouts every single month."
                            },
                            {
                                icon: <Globe className="w-10 h-10 text-orange-600" />,
                                title: "Global Reach",
                                desc: "Ads delivered with optimized CPMs across multiple geolocations."
                            },
                            {
                                icon: <Rocket className="w-10 h-10 text-orange-600" />,
                                title: "Innovative Solutions",
                                desc: "We constantly improve to bring futuristic formats and AI-driven optimization."
                            }
                        ].map((item, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, y: 40 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.8, delay: index * 0.1 }}
                                className="bg-white/70 backdrop-blur-lg p-8 rounded-3xl border border-orange-200 shadow-lg hover:shadow-xl transition-all duration-300"
                            >
                                <div className="flex justify-center mb-4">{item.icon}</div>
                                <h3 className="text-xl font-bold text-gray-900 mb-2">{item.title}</h3>
                                <p className="text-gray-600">{item.desc}</p>
                            </motion.div>
                        ))}
                    </motion.div>

                    {/* Final CTA */}
                    <motion.div variants={fadeInUp} className="pt-12">
                        <a
                            href="https://dashboard.monitize-media.com/signup"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <motion.button
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                className="px-10 py-4 bg-gradient-to-r from-orange-500 to-orange-600 text-white font-bold rounded-xl hover:from-orange-600 hover:to-orange-700 transition-all duration-300 shadow-lg hover:shadow-xl hover:shadow-orange-200 text-lg flex items-center space-x-2 mx-auto"
                            >
                                <span>Join Monitize Media</span>
                            </motion.button>
                        </a>
                    </motion.div>
                </motion.div>

            </section>
            <Footer></Footer>
        </>
    );
};

export default AboutUs;
